/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Top-level shell for the redesigned Settings modal (Figma file
// Y95LG1oFzU2PHIvFuMYTfJ, page "Settings modal"). Pinned to Figma:
//
//   * Scrim   — node 3542:7347, black at 0.25 opacity.
//   * Modal   — node 3542:7348, 1200×720, two-column layout
//               (sidebar 301px / main pane 899px). Border-radius 16
//               on the outer card.
//   * Header  — node 3542:7357, height 28 + 24px top padding +
//               24px horizontal padding. Heading text Lato Bold
//               20/28 #F5F6F8.
//
// The "Appearance" pane is the only one whose state is lifted out
// because flipping the theme has to ripple to document.documentElement
// from the host (apps/web/.../SettingsModalHost.tsx). The other panes
// keep local state until the rest of the panes are wired to a shared
// preferences store.

import { HelpCircle, X } from "lucide-react-native";
import { useEffect, useMemo, useState } from "react";
// SETTINGS_TAB_LABELS is no longer rendered in the modal header —
// each pane owns its own title as the first SettingsRow per Figma
// node 3542:7357 (the header strip is just a 28×28 close button on
// the right edge). Import stays for SettingsTabId only.
import {
	Modal,
	Pressable,
	ScrollView,
	StyleSheet,
	View,
} from "react-native";
import { AccountPane, type ProfileEditInput } from "./panes/AccountPane";
import {
	AppearancePane,
	type AppearanceBackground,
	type AppearanceTheme,
} from "./panes/AppearancePane";
import {
	type EncryptionDevice,
	EncryptionPane,
} from "./panes/EncryptionPane";
import {
	type LinkingAcceptInput,
	type LinkingBuildInput,
	type LinkingBuildResult,
	LinkingPane,
} from "./panes/LinkingPane";
import { NotificationsPane, type NotificationsPaneProps } from "./panes/NotificationsPane";
import { KeyboardShortcutsPane } from "./panes/KeyboardShortcutsPane";
import { HelpAboutPane } from "./panes/HelpAboutPane";
import { PlaceholderPane } from "./panes/PlaceholderPane";
import { PreferencesPane, type PreferencesPaneProps } from "./panes/PreferencesPane";
import { PrivacyPane } from "./panes/PrivacyPane";
import { StoragePane, type StoragePaneProps } from "./panes/StoragePane";
import { SidebarPane } from "./panes/SidebarPane";
import {
	type RecoveryBackupStatus,
	type RecoveryUploadInput,
	type RecoveryUploadResult,
	RecoveryPane,
} from "./panes/RecoveryPane";
import { SessionsPane, type SessionsPaneDevice } from "./panes/SessionsPane";
import { SettingsSidebar } from "./SettingsSidebar";
import { type SettingsTabId } from "./types";

export interface SettingsModalProps {
	readonly visible: boolean;
	readonly onClose: () => void;
	readonly initialTab?: SettingsTabId;
	readonly accountDisplayName: string;
	readonly accountEmail: string;
	readonly accountPhone: string;
	readonly accountAvatarUrl?: string;
	/** True while a freshly-picked avatar is uploading → shimmer in the pane. */
	readonly accountAvatarUploading?: boolean;
	/** Editable profile-detail fields + flags (seeded from GET /v1/me/profile). */
	readonly accountBio?: string;
	readonly accountJobTitle?: string;
	readonly accountCompany?: string;
	readonly accountDiscoverable?: boolean;
	/** Toggle search discoverability (`discoverable`). */
	readonly onToggleDiscoverable?: (next: boolean) => Promise<void> | void;
	/**
	 * Persona KYC status fetched from `GET /v1/me/kyc`.
	 * Omit to hide the KYC row (Persona not configured on this env).
	 */
	readonly accountKycStatus?: 'none' | 'pending' | 'needs_review' | 'approved' | 'declined';
	/**
	 * Initiate KYC — the host creates the inquiry (`POST /v1/me/kyc`) and
	 * opens the embedded Persona flow in an in-page modal. Resolves once the
	 * inquiry has been launched.
	 */
	readonly onKycStart?: () => Promise<void>;
	/** Open the recovery-passphrase backup flow (Ping is passwordless). */
	readonly onOpenRecovery?: () => void;
	/** Replay the first-run app tour from Help & About. Omit to hide the button. */
	readonly onReplayHomeTour?: () => void;
	/** Replay the settings walkthrough from Help & About. Omit to hide the button. */
	readonly onReplaySettingsTour?: () => void;
	/**
	 * Open the detailed guide for the currently-visible pane, triggered by the
	 * floating guide button. Omit to hide the button (e.g. on platforms without
	 * the tour system wired up).
	 */
	readonly onOpenPaneGuide?: (pane: SettingsTabId) => void;
	readonly onDisplayNameSaved: (next: string) => void;
	readonly onSignOutAllDevices: () => Promise<void>;
	/**
	 * Sign out a specific list of devices belonging to the calling
	 * account. Wired to Settings → Sessions multi-select "Log out".
	 * Optional — when omitted the pane disables the destructive
	 * button unless the selection covers every other session.
	 */
	readonly onSignOutDevices?: (deviceIds: readonly string[]) => Promise<void>;
	// Sign-out for the current device only — the LogOut icon in the
	// sidebar's account card. Distinct from `onSignOutAllDevices`
	// which lives in the Sessions pane.
	readonly onSignOutCurrentDevice: () => Promise<void>;
	/** Zero-residue full local wipe of THIS device ("Erase this device") — the
	 *  counterpart to the preserve-resume sign-out, for a shared/at-risk browser.
	 *  Optional: when omitted the Sessions pane hides the action. */
	readonly onEraseThisDevice?: () => Promise<void>;
	readonly currentDeviceLabel: string;
	readonly currentDeviceSubtitle: string;
	// Host-supplied save handler — runs the PATCH /v1/me/profile call.
	readonly onUpdateProfile: (input: ProfileEditInput) => Promise<void>;
	// Host-supplied loader for the Encryption pane device list.
	readonly loadDevices: () => Promise<{
		readonly currentDeviceId: string | null;
		readonly devices: readonly EncryptionDevice[];
	}>;
	// Host-supplied loader for the Sessions pane device list. Distinct
	// from `loadDevices` above (Encryption pane uses MLS-leaf metadata
	// from `/v1/users/{id}/devices`; Sessions pane uses auth-layer
	// sessions from `GET /v1/auth/sessions`).
	readonly loadSessions?: () => Promise<readonly SessionsPaneDevice[]>;
	// Optional avatar-pick handler. Undefined hides the "Change" button
	// on platforms without a file picker (desktop, until we add one).
	readonly onAvatarPicked?: () => void | Promise<void>;
	// Optional press handler that fires when the user taps "Change"
	// avatar. Web hosts use this to open a hidden `<input type=file>`
	// (the RN-side has no equivalent control); the host then calls
	// `onAvatarPicked` after the user selects a file. Desktop leaves
	// it undefined.
	readonly onAvatarPress?: () => void;
	// Controlled Appearance state. The host owns the persistence layer
	// (preferences store on web, KV store on desktop) and is also
	// responsible for flipping `document.documentElement` attributes
	// on web so NativeWind's dark-mode utility + the design-token CSS
	// variable palette react to the change.
	readonly theme: AppearanceTheme;
	readonly onThemeChange: (next: AppearanceTheme) => void;
	readonly accent: string;
	readonly onAccentChange: (next: string) => void;
	readonly background: AppearanceBackground;
	readonly onBackgroundChange: (next: AppearanceBackground) => void;
	readonly fontScale: number;
	readonly onFontScaleChange: (next: number) => void;
	/**
	 * Linking pane wire-up. Both halves of the link happen on Ping
	 * devices — `onBuildLinkingTicket` runs on the existing device,
	 * `onAcceptLinkingTicket` (optional) runs on the new device.
	 * Hosts that don't ship the new-device side from Settings (it's
	 * normally part of the auth flow) omit the accept callback.
	 */
	readonly onBuildLinkingTicket?: (input: LinkingBuildInput) => Promise<LinkingBuildResult>;
	readonly onAcceptLinkingTicket?: (input: LinkingAcceptInput) => Promise<void>;
	/**
	 * Optional realtime watcher: the LinkingPane invokes this after
	 * `onBuildLinkingTicket` resolves, passing the new device id and
	 * an `AbortSignal`. Resolves with the device label when the new
	 * device's `POST /v1/auth/link-verify` completes (i.e. the row
	 * shows up in `GET /v1/auth/sessions`), or `null` on timeout.
	 * Web hosts poll the sessions endpoint; native hosts can plug a
	 * WebSocket-based notification later. When omitted the pane
	 * stays on the static "Pairing…" copy until the user navigates
	 * to Sessions manually.
	 */
	readonly onWatchForLinkPaired?: (
		input: { newDeviceIdBase64Url: string },
		signal: AbortSignal,
	) => Promise<{ label: string } | null>;
	/**
	 * Host-injected QR renderer for the Linked Devices pane. Web wires
	 * `qrcode` (canvas/SVG), desktop wires `react-native-qrcode-svg`.
	 * When omitted the pane hides the QR affordance and falls back to
	 * paste-only.
	 */
	readonly renderQrCode?: (value: string, size: number) => JSX.Element;
	/**
	 * Host-injected camera scanner trigger. Returns the scanned text
	 * (raw payload — the pane validates) or null if dismissed. When
	 * omitted scanning is unavailable and only paste shows.
	 */
	readonly onScanRequest?: () => Promise<string | null>;
	/**
	 * Recovery-blob upload wire-up. Optional — omit on platforms where
	 * WebCrypto (`crypto.subtle`) isn't available so the pane doesn't
	 * render an unusable form. Download/restore lives on the auth flow
	 * (`POST /v1/auth/recover/verify`) and is not exposed here.
	 */
	readonly onUploadRecoveryBlob?: (input: RecoveryUploadInput) => Promise<RecoveryUploadResult>;
	/**
	 * Host-supplied clipboard writer. Wired to the platform's native
	 * clipboard so this module stays free of `react-native-clipboard`
	 * imports. Required when `onUploadRecoveryBlob` is set.
	 */
	readonly onCopyRecoveryKey?: (text: string) => Promise<void> | void;
	/**
	 * Host-supplied file download for the recovery key text. Wired to
	 * an anchor blob download on web; macOS host can implement with
	 * NSSavePanel. Required when `onUploadRecoveryBlob` is set.
	 */
	readonly onDownloadRecoveryKey?: (text: string, filename: string) => Promise<void> | void;
	/**
	 * Current recovery-backup status from the host's local marker
	 * store. Omit to hide the status row entirely.
	 */
	readonly recoveryBackupStatus?: RecoveryBackupStatus;
	/**
	 * Reveal the locally-stored recovery phrase (V3 mnemonic flow).
	 * Resolves to the 12 words, or null if nothing is stored. When set
	 * alongside `onRotateRecoveryKey`, the Recovery pane renders the
	 * modern reveal/rotate surface instead of the legacy passphrase form.
	 */
	readonly onRevealRecoveryMnemonic?: () => Promise<string | null>;
	/** Persist "user saved the phrase" + purge the stored mnemonic. */
	readonly onConfirmRecoveryMnemonicSaved?: () => Promise<void> | void;
	/** Rotate the recovery key (V3); resolves to the NEW mnemonic. */
	readonly onRotateRecoveryKey?: () => Promise<string>;
	/** Controlled notification preferences for the Notifications pane. The
	 *  host owns persistence (web `PreferencesStore`, desktop controller). When
	 *  omitted the pane falls back to uncontrolled local state. */
	readonly notifications?: NotificationsPaneProps;
	/** Controlled behaviour preferences for the Preferences pane (Enter-to-send,
	 *  auto-download). Host-persisted like `notifications`; omitted → local
	 *  uncontrolled state. */
	readonly preferences?: PreferencesPaneProps;
	/** Storage pane data + actions (media-cache usage + clear). Host-supplied
	 *  because cache measurement/clearing is platform-specific. */
	readonly storage?: StoragePaneProps;
	/** Open the Set Status overlay from within the Account pane. */
	readonly onSetStatusPress?: () => void;
	/** Current status to show in the Account pane status row. */
	readonly currentStatusEmoji?: string;
	readonly currentStatusText?: string;
}

export function SettingsModal(props: SettingsModalProps): JSX.Element | null {
	const {
		visible,
		onClose,
		initialTab = "account",
		accountDisplayName,
		accountEmail,
		accountPhone,
		accountAvatarUrl,
		accountAvatarUploading,
		accountBio,
		accountJobTitle,
		accountCompany,
		accountDiscoverable,
		onToggleDiscoverable,
		accountKycStatus,
		onKycStart,
		onOpenRecovery,
		onReplayHomeTour,
		onReplaySettingsTour,
		onOpenPaneGuide,
		onDisplayNameSaved,
		onEraseThisDevice,
		onSignOutAllDevices,
		onSignOutDevices,
		onSignOutCurrentDevice,
		currentDeviceLabel,
		currentDeviceSubtitle,
		onUpdateProfile,
		loadDevices,
		loadSessions,
		onAvatarPicked,
		onAvatarPress,
		theme,
		onThemeChange,
		accent,
		onAccentChange,
		background,
		onBackgroundChange,
		fontScale,
		onFontScaleChange,
		onBuildLinkingTicket,
		onAcceptLinkingTicket,
		onWatchForLinkPaired,
		onUploadRecoveryBlob,
		onCopyRecoveryKey,
		onDownloadRecoveryKey,
		recoveryBackupStatus,
		onRevealRecoveryMnemonic,
		onConfirmRecoveryMnemonicSaved,
		onRotateRecoveryKey,
		renderQrCode,
		onScanRequest,
		onSetStatusPress,
		currentStatusEmoji,
		currentStatusText,
	} = props;

	const [active, setActive] = useState<SettingsTabId>(initialTab);
	const [sessionDevices, setSessionDevices] = useState<
		readonly SessionsPaneDevice[] | null
	>(null);

	useEffect(() => {
		if (!visible) setActive(initialTab);
	}, [visible, initialTab]);

	// Lazy-load the sessions device list the first time the Sessions
	// tab activates. Refetch on every reopen so a sign-in from another
	// device shows up promptly. Host-supplied `loadSessions` is
	// optional — when omitted the pane renders the legacy
	// single-device card.
	useEffect(() => {
		if (!visible || active !== "session" || loadSessions === undefined) {
			return;
		}
		let cancelled = false;
		loadSessions()
			.then((next) => {
				if (!cancelled) setSessionDevices(next);
			})
			.catch(() => {
				// Swallow — the pane gracefully falls back to the legacy
				// single-device card on null. The host's own logging
				// path captures the underlying network error.
				if (!cancelled) setSessionDevices(null);
			});
		return () => {
			cancelled = true;
		};
	}, [visible, active, loadSessions]);

	const pane = useMemo(() => {
		switch (active) {
			case "account":
				return (
					<AccountPane
						initialDisplayName={accountDisplayName}
						email={accountEmail}
						phone={accountPhone}
						{...(accountAvatarUrl !== undefined
							? { avatarUrl: accountAvatarUrl }
							: {})}
						{...(accountAvatarUploading !== undefined
							? { avatarUploading: accountAvatarUploading }
							: {})}
						{...(accountBio !== undefined ? { bio: accountBio } : {})}
						{...(accountJobTitle !== undefined ? { jobTitle: accountJobTitle } : {})}
						{...(accountCompany !== undefined ? { company: accountCompany } : {})}
						{...(accountDiscoverable !== undefined
							? { discoverable: accountDiscoverable }
							: {})}
						{...(onToggleDiscoverable !== undefined ? { onToggleDiscoverable } : {})}
						{...(accountKycStatus !== undefined ? { kycStatus: accountKycStatus } : {})}
						{...(onKycStart !== undefined ? { onKycStart } : {})}
						onOpenRecovery={onOpenRecovery ?? (() => setActive("recovery"))}
						{...(onAvatarPicked !== undefined ? { onAvatarPicked } : {})}
						{...(onAvatarPress !== undefined ? { onAvatarPress } : {})}
						onDisplayNameSaved={onDisplayNameSaved}
						onUpdateProfile={onUpdateProfile}
						{...(onSetStatusPress !== undefined ? { onSetStatusPress } : {})}
						{...(currentStatusEmoji !== undefined ? { currentStatusEmoji } : {})}
						{...(currentStatusText !== undefined ? { currentStatusText } : {})}
					/>
				);
			case "session":
				return (
					<SessionsPane
						currentDeviceLabel={currentDeviceLabel}
						currentDeviceSubtitle={currentDeviceSubtitle}
						onSignOutAllDevices={onSignOutAllDevices}
						onSignOutCurrentDevice={onSignOutCurrentDevice}
						{...(onEraseThisDevice !== undefined ? { onEraseThisDevice } : {})}
						{...(onSignOutDevices !== undefined
							? { onSignOutDevices }
							: {})}
						{...(loadSessions !== undefined
							? { onRefreshDevices: loadSessions }
							: {})}
						{...(sessionDevices !== null
							? { devices: sessionDevices }
							: {})}
					/>
				);
			case "appearance":
				return (
					<AppearancePane
						theme={theme}
						onThemeChange={onThemeChange}
						accent={accent}
						onAccentChange={onAccentChange}
						background={background}
						onBackgroundChange={onBackgroundChange}
						fontScale={fontScale}
						onFontScaleChange={onFontScaleChange}
					/>
				);
			case "notifications":
				return <NotificationsPane {...(props.notifications ?? {})} />;
			case "preferences":
				return <PreferencesPane {...(props.preferences ?? {})} />;
			case "storage":
				return <StoragePane {...(props.storage ?? {})} />;
			case "securityPrivacy":
				return <PrivacyPane />;
			case "encryption":
				return <EncryptionPane loadDevices={loadDevices} />;
			case "linking":
				if (onBuildLinkingTicket === undefined) {
					return (
						<PlaceholderPane
							title="Linked devices"
							description="Device linking is not wired on this surface."
						/>
					);
				}
				return (
					<LinkingPane
						onBuildTicket={onBuildLinkingTicket}
						{...(onAcceptLinkingTicket !== undefined
							? { onAcceptTicket: onAcceptLinkingTicket }
							: {})}
						{...(renderQrCode !== undefined ? { renderQrCode } : {})}
						{...(onScanRequest !== undefined ? { onScanRequest } : {})}
						{...(onWatchForLinkPaired !== undefined
							? { onWatchForPaired: onWatchForLinkPaired }
							: {})}
					/>
				);
			case "recovery":
				if (
					onUploadRecoveryBlob === undefined ||
					onCopyRecoveryKey === undefined ||
					onDownloadRecoveryKey === undefined
				) {
					return (
						<PlaceholderPane
							title="Account recovery"
							description="Recovery backup is not wired on this surface."
						/>
					);
				}
				return (
					<RecoveryPane
						onUpload={onUploadRecoveryBlob}
						onCopyToClipboard={onCopyRecoveryKey}
						onDownload={onDownloadRecoveryKey}
						{...(recoveryBackupStatus !== undefined
							? { status: recoveryBackupStatus }
							: {})}
						{...(onRevealRecoveryMnemonic !== undefined
							? { onRevealMnemonic: onRevealRecoveryMnemonic }
							: {})}
						{...(onConfirmRecoveryMnemonicSaved !== undefined
							? { onConfirmMnemonicSaved: onConfirmRecoveryMnemonicSaved }
							: {})}
						{...(onRotateRecoveryKey !== undefined
							? { onRotateRecoveryKey }
							: {})}
					/>
				);
			case "keyboard":
				return <KeyboardShortcutsPane />;
			case "sidebar":
				return <SidebarPane />;
			case "voiceVideo":
				return (
					<PlaceholderPane
						testID="settings-voiceVideo-overview"
						title="Voice & Video"
						description="Default microphone, camera, and ring tone selection."
					/>
				);
			case "helpAbout":
				return (
					<HelpAboutPane
						{...(onReplayHomeTour !== undefined ? { onReplayHomeTour } : {})}
						{...(onReplaySettingsTour !== undefined ? { onReplaySettingsTour } : {})}
					/>
				);
		}
	}, [
		active,
		accent,
		accountAvatarUrl,
		accountAvatarUploading,
		accountBio,
		accountCompany,
		accountDiscoverable,
		accountDisplayName,
		accountEmail,
		accountJobTitle,
		accountPhone,
		onToggleDiscoverable,
		accountKycStatus,
		onKycStart,
		onOpenRecovery,
		onReplayHomeTour,
		onReplaySettingsTour,
		background,
		currentDeviceLabel,
		currentDeviceSubtitle,
		fontScale,
		loadDevices,
		loadSessions,
		onAccentChange,
		onAcceptLinkingTicket,
		onAvatarPicked,
		onAvatarPress,
		onBackgroundChange,
		onBuildLinkingTicket,
		onDisplayNameSaved,
		onFontScaleChange,
		onScanRequest,
		onSignOutAllDevices,
		onSignOutDevices,
		onThemeChange,
		onUpdateProfile,
		onUploadRecoveryBlob,
		onCopyRecoveryKey,
		onDownloadRecoveryKey,
		recoveryBackupStatus,
		onRevealRecoveryMnemonic,
		onConfirmRecoveryMnemonicSaved,
		onRotateRecoveryKey,
		renderQrCode,
		sessionDevices,
		theme,
	]);

	if (!visible) return null;

	return (
		<Modal
			visible={visible}
			transparent
			// "none" pops the portal instantly. RN-Web's "fade" applies
			// a 300ms CSS opacity transition that made the Settings UI
			// feel sluggish on open; the scrim + card already provide
			// enough visual landing.
			animationType="none"
			onRequestClose={onClose}
			// react-native-macos and react-native-web both forward
			// Escape to onRequestClose — no extra keydown listener.
		>
			<Pressable
				accessibilityRole="none"
				onPress={onClose}
				style={StyleSheet.absoluteFill}
				// Figma scrim — pure black at 25% opacity (node 3542:7347).
				className="
					z-50 flex-1 items-center justify-center p-6
					bg-black/[0.25]
				"
			>
				<Pressable
					onPress={() => undefined}
					// Outer modal card — Figma node 3542:7348. 1200×720,
					// 16px corner radius, no border (sidebar + main pane
					// own their own surfaces).
					className="
						flex flex-row w-full max-w-[1200px] h-full max-h-[720px]
						rounded-2xl overflow-hidden
						bg-light-surface-primary dark:bg-[#11131A]
						shadow-2xl
					"
				>
					<SettingsSidebar
						active={active}
						onChange={setActive}
						accountDisplayName={accountDisplayName}
						accountSubtitle={accountEmail || accountPhone || ""}
						onSignOut={() => void onSignOutCurrentDevice()}
						{...(onEraseThisDevice !== undefined
							? { onEraseThisDevice: () => void onEraseThisDevice() }
							: {})}
					/>

					<View className="relative flex-1 min-w-0 flex flex-col bg-light-surface-primary dark:bg-[#11131A]">
						<View
							// "Exit" row (Figma 3542:4420) — main screen has
							// padding 24, gap 8 between exit row and container.
							// Render the close affordance flush right; each
							// pane owns its own heading as the first row.
							className="
								flex flex-row items-center justify-end
								px-6 pt-6 pb-2
							"
						>
							<Pressable
								accessibilityRole="button"
								accessibilityLabel="Close settings"
								onPress={onClose}
								// Close button — Figma 3542:4421 (24×28, radius
								// 12, padding 4). We round to w-6 h-7 to match.
								className="
									w-6 h-7 inline-flex items-center justify-center
									rounded-xl
									hover:bg-light-surface-tertiary/60 dark:hover:bg-white/[0.06]
									transition-colors
								"
							>
								<X
									size={18}
									strokeWidth={1.75}
									color="currentColor"
									className="text-light-text-secondary dark:text-[#B5BAC3]"
								/>
							</Pressable>
						</View>

						<ScrollView
							className="flex-1 min-h-0"
							contentContainerStyle={{ paddingHorizontal: 24, paddingBottom: 24 }}
						>
							{pane}
						</ScrollView>

						{/* Floating per-pane guide button (Figma-agnostic helper).
						    Tapping it walks the current pane's sections. */}
						{onOpenPaneGuide !== undefined && (
							<Pressable
								testID="settings-pane-guide"
								accessibilityRole="button"
								accessibilityLabel="Show a guide for this section"
								onPress={() => onOpenPaneGuide(active)}
								className="
									absolute bottom-6 right-6
									w-11 h-11 rounded-full
									flex items-center justify-center
									bg-light-accent-brand dark:bg-dark-accent-brand
									shadow-lg active:opacity-80
								"
							>
								<HelpCircle size={22} strokeWidth={2} color="#FFFFFF" />
							</Pressable>
						)}
					</View>
				</Pressable>
			</Pressable>
		</Modal>
	);
}
